// /src/js/utils/environment.js

/**
 * Unified environment detection and configuration
 * Only two environments: DEV and PROD
 * Local development connects to DEV by default, can override via localStorage
 */

/**
 * The environment is read from the SHAPE of the host, never from a list of our
 * domains (vitrina-vue#158, owner rule 2026-10-28): this bundle is public, and a
 * list of every storefront domain in it would hand the whole portfolio to anyone
 * who reads it. The edge's dev service answers `dev.<domain>` and the app's dev
 * hosts are `appdev.<domain>`; App Engine version URLs put `dev` in the
 * `-dot-` chain (`dev-dot-<project>`, `<version>-dot-dev-dot-<project>`). Any
 * other host is production — fail-closed, the polarity isDevHost() already had.
 * Server-side checks against the real host lists live in vitrina-gae-cdn.
 */
const LOCAL_HOSTNAME = /(^|\.)localhost$|^127\.0\.0\.1$|^\[?::1\]?$/;
const DEV_LABELS = new Set(['dev', 'appdev']);

/**
 * Browsers keep a fully-qualified host's trailing dot in location.hostname
 * (`localhost.`, `dev.example.com.`); the shape is the same host without it.
 * @param {string} hostname
 * @returns {string}
 */
function normalizedHostname(hostname) {
  return hostname.toLowerCase().replace(/\.$/, '');
}

/**
 * @param {string} hostname
 * @returns {boolean} - True for localhost, 127.0.0.1 and ::1 (and *.localhost).
 */
function isLocalHostname(hostname) {
  return LOCAL_HOSTNAME.test(normalizedHostname(hostname));
}

/**
 * @param {string} hostname
 * @returns {boolean} - True when the first DNS label, split on App Engine's
 *   `-dot-`, carries `dev` or `appdev` (dev.x.mx, appdev.x.tools, dev-dot-p,
 *   v1-dot-dev-dot-p). Never true for a plain production or customer domain.
 */
function isDevShapedHostname(hostname) {
  const firstLabel = normalizedHostname(hostname).split('.')[0];
  return firstLabel.split('-dot-').some((part) => DEV_LABELS.has(part));
}

/**
 * Detect current environment based on hostname and localStorage
 * @returns {'PROD'|'DEV'} - Environment string (only two environments)
 */
export function detectEnvironment() {
  const hostname = window.location.hostname;

  // Local development - check localStorage override, default to DEV
  if (isLocalHostname(hostname)) {
    return localStorage.getItem('ENVIRONMENT') === 'PROD' ? 'PROD' : 'DEV';
  }

  if (isDevShapedHostname(hostname)) {
    return 'DEV';
  }

  // Every other host — portfolio apex, www., a customer's own domain, an
  // appspot version URL — is production.
  return 'PROD';
}

/**
 * True only on dev-shaped hosts — the vitrina edge's dev service serves
 * dev.{domain} (plus appdev.* app hosts, the dev-dot- appspot forms and local
 * dev). FAIL-CLOSED: any other host counts as production, so a feature gated
 * on it can never show in production (e.g. the add-to-calendar test button,
 * René 2026-08-12). Since #158 detectEnvironment() applies the same rule.
 * @returns {boolean}
 */
export function isDevHost() {
  const hostname = window.location.hostname;
  return isLocalHostname(hostname) || isDevShapedHostname(hostname);
}

/**
 * Check if we're running in local development
 * @returns {boolean}
 */
export function isLocalDevelopment() {
  return isLocalHostname(window.location.hostname);
}

/**
 * Check if we're running on a production domain (no overrides allowed)
 * @returns {boolean}
 */
export function isProductionDomainCurrent() {
  return !isDevHost();
}

/**
 * Base URL for the VITRINA's own edge backend (the /api service on the
 * vitrina-gae-cdn GAE service). This is NOT the salon-staff API (runapis) — the
 * vitrina talks only to its own isolated surface.
 *
 * - localhost: no local GAE runs, so hit the deployed DEV edge directly.
 * - dev / prod domains: same-origin ('' → /api is on the same GAE service).
 *
 * @returns {string} - base to prefix before '/api/...' (may be '')
 */
/**
 * The public URL of a legal document served by loginsalon's /legal registry.
 *
 * loginsalon runs as a single PROD service with no DEV twin, so the host is the same in
 * both environments and only the `env` query parameter differs. That parameter is added
 * ONLY on a dev host: the registry treats an absent value as PROD, which keeps a customer
 * from ever seeing a URL that reveals we run a second environment.
 *
 * Passing edition + version returns the IMMUTABLE permalink for that exact text, which the
 * registry serves forever including superseded versions — a customer who accepted 1.0.001
 * must be able to read 1.0.001, not whatever replaced it. Path segments rather than query
 * params on purpose: the document is a permanent resource, and `lang`/`env` are variants
 * of it.
 *
 * @param {string} docType - Registry document type, e.g. 'tyc-usuarios'.
 * @param {Object} [pinned] - Pin a specific published version.
 * @param {string} [pinned.edition]
 * @param {string} [pinned.version]
 * @returns {string} - Absolute URL, safe to open in a new tab.
 */
export function getLegalDocumentUrl(docType, { edition, version, close } = {}) {
  const path = edition && version
    ? `${docType}/${edition}/${version}`
    : docType;
  // legal.salon.now — the branded home for anything a CUSTOMER sees or a contract cites.
  // It is a GAE custom-domain alias of login.salon.tools, so both hosts serve the identical
  // app and anything already recorded against the old host keeps resolving. The `/legal`
  // prefix stays because the app is mounted by PATH prefix (loginsalon srv-default.py), not
  // by host; dropping it would mean host-aware routing inside a production auth service.
  //
  // These URLs go into confirmation emails and are cited as evidence, so the shape is a
  // permanence decision, not cosmetics.
  const url = `https://legal.salon.now/legal/${path}`;
  const params = [];
  if (isDevelopment()) params.push('env=DEV');
  // `close=true` makes the legal shell offer a "Cerrar ventana" button (login#66).
  // Opt-in per call site: only links that OPEN A TAB from a flow the reader needs
  // to get back to. Permalinks cited in records and emails stay clean — the URL
  // shape there is evidence, and a UI affordance does not belong in it.
  if (close) params.push('close=true');
  return params.length ? `${url}?${params.join('&')}` : url;
}

export function getVitrinaApiBase() {
  if (isLocalDevelopment()) {
    return 'https://dev.peluquerias.mx';
  }
  // On a real vitrina domain the /api lives in the same service → same-origin.
  return '';
}

/**
 * Get the telemetry ingest URL: this storefront's OWN origin (#137).
 *
 * The edge proxies `/api/telemetry/*` to the ingest service (vitrina-gae-cdn
 * `telemetry_proxy.py`, #77), the same way it fronts every other call the SPA
 * makes. Nothing behind the proxy — no runapis, no telemetry host — is ever
 * named in this bundle or in the CSP; a visitor reading either sees only the
 * storefront. On localhost the base is the dev edge (getVitrinaApiBase).
 *
 * The client appends `/v1/events`, unchanged from the direct-to-ingest days.
 * @returns {string}
 */
export function getTelemetryServerUrl() {
  return `${getVitrinaApiBase()}/api/telemetry`;
}

/**
 * Online mode constants (ported from app-salon-tools so the telemetry client
 * shares one online-detection vocabulary across the two apps).
 */
export const ONLINE_MODE = {
  ONLINE: 1,           // Network available, use remote servers
  OFFLINE_FORCED: 2,   // User/system forced offline mode (future: for network intermittency)
  OFFLINE_DETECTED: 3  // Browser detected offline via navigator.onLine
};

/**
 * Detect current online/offline mode.
 * The vitrina is a plain SPA (no offline-first PWA), so this is a thin wrapper
 * over navigator.onLine — kept API-compatible with app-salon-tools.
 * @returns {number} - Online mode (1=Online, 2=Offline Forced, 3=Offline Detected)
 */
export function detectOnlineMode() {
  if (navigator.onLine) {
    return ONLINE_MODE.ONLINE;
  }
  return ONLINE_MODE.OFFLINE_DETECTED;
}

/**
 * Get client/frontend URL: always this page's own origin. Every host the
 * storefront runs on — local (usually port 6489), dev-shaped, a portfolio apex,
 * a customer's own domain — serves the client from itself, so there is no
 * fallback to a named app host (and no host list, #158).
 * @returns {string} - Client frontend URL
 */
export function getClientUrl() {
  return `${window.location.protocol}//${window.location.host}`;
}

/**
 * Check if running in development mode
 * @returns {boolean}
 */
export function isDevelopment() {
  const env = detectEnvironment();
  return env === 'DEV' || isLocalDevelopment() || import.meta.env.DEV;
}

/**
 * Check if running in production mode
 * @returns {boolean}
 */
export function isProduction() {
  const env = detectEnvironment();
  return env === 'PROD' && !isLocalDevelopment() && import.meta.env.PROD;
}

/**
 * Get complete environment configuration
 * @returns {object} - Environment configuration object
 */
export function getEnvironmentConfig() {
  const environment = detectEnvironment();
  const isLocal = isLocalDevelopment();
  
  return {
    environment,
    isLocalDevelopment: isLocal,
    clientUrl: getClientUrl(),
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    // Additional config flags
    enableDevtools: !isProduction(),
    enableConsoleDebug: !isProduction(),
    enableServiceWorker: isProduction() && !isLocal,
    enableAnalytics: isProduction() && !isLocal,
    // Local storage overrides info
    environmentOverride: isLocal ? localStorage.getItem('ENVIRONMENT') : null
  };
}

/**
 * Utility functions for managing localStorage overrides
 */
export const environmentOverrides = {
  /**
   * Set environment override for local development and development domains
   * @param {'DEV'|'PROD'|null} env - Environment to override to
   */
  setEnvironment(env) {
    // Only production domains are restricted
    if (isProductionDomainCurrent()) {
      console.warn('Cannot override server configuration on production domains');
      return;
    }
    
    // Allow on localhost and development domains
    if (!isDevHost()) {
      console.warn('Environment overrides only work on localhost or development domains');
      return;
    }
    
    if (env === 'DEV' || env === 'PROD') {
      localStorage.setItem('ENVIRONMENT', env);
    } else {
      localStorage.removeItem('ENVIRONMENT');
    }
    
    console.log(`Environment override set to: ${env || 'default (DEV)'}`);
  },
  
  /**
   * Set development server URL override
   * @param {string|null} serverUrl - Server URL to override to
   */
  setDevServer(serverUrl) {
    // Only production domains are restricted
    if (isProductionDomainCurrent()) {
      console.warn('Cannot override server configuration on production domains');
      return;
    }
    
    // Allow on localhost and development domains
    if (!isDevHost()) {
      console.warn('Dev server overrides only work on localhost or development domains');
      return;
    }
    
    if (serverUrl) {
      localStorage.setItem('DEV_SERVER', serverUrl);
    } else {
      localStorage.removeItem('DEV_SERVER');
    }
    
    console.log(`Dev server override set to: ${serverUrl || 'default'}`);
  },
  
  /**
   * Clear all overrides
   */
  clearAll() {
    localStorage.removeItem('ENVIRONMENT');
    localStorage.removeItem('DEV_SERVER');
    console.log('All environment overrides cleared');
  },
  
  /**
   * Get current overrides
   */
  getCurrent() {
    return {
      environment: localStorage.getItem('ENVIRONMENT'),
      devServer: localStorage.getItem('DEV_SERVER')
    };
  }
};

// Export singleton instance
export const ENV_CONFIG = getEnvironmentConfig();

// Global environment variable for backward compatibility
export const ENV = detectEnvironment();